import {
  type SankeyNode as DSankeyNode,
  sankey,
  sankeyJustify,
  sankeyLinkHorizontal,
} from 'd3-sankey';
import cloneDeep from 'lodash/cloneDeep';
import { useMemo } from 'react';

import { getTextWidth } from '../../utilities';
import { findOrderInversions, type OrderItem } from './sankey-inversions';
import {
  applyColumnFillLayout,
  EMPTY_LINK_ENDPOINTS,
  floorLinkValue,
  interiorLabelScale,
  isLinearSankeyScale,
  scaleLinkValues,
  solveMinLinkValue,
} from './sankey-layout';
import { LABEL_PADDING, VALUE_FONT_SIZE } from './sankey-node-label';
import type { SankeyNodeRaw } from './sankey-node-tooltip';
import { solveSankeyScale } from './sankey-scale-solver';
import { validateLinks, validateNodes } from './sankey-validator';
import {
  getSankeyNodeLabel,
  type SankeyLink as Link,
  type SankeyNode as Node,
  type SankeyData,
  type SankeyLinkValidated,
  type SankeyNodeValidated,
  type SankeyRenderLink,
  type SankeyScale,
} from './types';

const PLOT_TOP_PADDING = 32;
export const PLOT_BOTTOM_PADDING = 8;
const NODE_WIDTH = 4;
const NODE_LINK_GAP = 2;
const SIDE_LABEL_MIN_CAP = 100;
const SIDE_LABEL_MAX_CAP = 140;
const SIDE_LABEL_WIDTH_SHARE = 0.13;
const SIDE_LABEL_FIT_SHARE = 0.8;
const SIDE_LABEL_MAX_WIDTH_SMALL = 30;
const SMALL_PLOT_WIDTH = 250;

const linkKeyFor = (link: SankeyLinkValidated): string =>
  `${link.source.id}__${link.target.id}`;

const outerColumnNodes = (
  data: SankeyData
): { first: Node[]; last: Node[] } => {
  const hasIncoming = new Set(data.links.map((link) => link.target));
  const hasOutgoing = new Set(data.links.map((link) => link.source));
  const maxLayer = data.nodes.reduce(
    (max, node) => Math.max(max, node.layer ?? 0),
    0
  );
  return {
    first: data.nodes.filter((node) =>
      node.layer === undefined ? !hasIncoming.has(node.id) : node.layer === 0
    ),
    last: data.nodes.filter((node) =>
      node.layer === undefined
        ? !hasOutgoing.has(node.id)
        : node.layer === maxLayer
    ),
  };
};

const nodeAlign = (node: DSankeyNode<Node, Link>, n: number): number =>
  node.layer ?? sankeyJustify(node, n);

const isIntermediate = (node: SankeyNodeValidated): boolean =>
  (node.sourceLinks?.length ?? 0) > 0 && (node.targetLinks?.length ?? 0) > 0;

const resolveHubIsTarget = (
  link: SankeyLinkValidated,
  columnSize: (depth: number) => number
): boolean => {
  if (isIntermediate(link.target)) return true;
  if (isIntermediate(link.source)) return false;
  return columnSize(link.target.depth) <= columnSize(link.source.depth);
};

export type SankeyLayout = {
  isPlotSizeValid: boolean;
  maxDepth: number;
  nodesValidated: SankeyNodeValidated[];
  renderLinks: SankeyRenderLink[];
  rawByNode: Map<string, SankeyNodeRaw>;
  interiorLabelMaxWidth: number;
  sideLabelMaxWidth: number;
  resolvedScale: SankeyScale;
  invertedLinkKeys: Set<string>;
  invertedNodeIds: Set<string>;
};

type UseSankeyLayoutArgs = {
  data: SankeyData | null;
  width: number;
  height: number;
  scale: SankeyScale;
  autoScale: boolean;
  fillColumnHeight: boolean;
  interiorLabelReserve: number;
  bottomReserve: number;
  showOrderInversions: boolean;
  inversionTolerance: number;
  labelFontSize: number | null;
  formatNodeValue?: (value: number) => string;
};

/** Runs the d3-sankey layout and derives everything the chart needs to render. */
export const useSankeyLayout = ({
  data,
  width,
  height,
  scale,
  autoScale,
  fillColumnHeight,
  interiorLabelReserve,
  bottomReserve,
  showOrderInversions,
  inversionTolerance,
  labelFontSize,
  formatNodeValue,
}: UseSankeyLayoutArgs): SankeyLayout => {
  const { totalExponent, shareExponent } = scale;
  return useMemo(() => {
    const hasData = !!data && data.nodes.length > 0 && data.links.length > 0;
    const hasViewport = width > 0 && height > 0;

    const isSmallPlot = width < SMALL_PLOT_WIDTH;
    const sideLabelCap = isSmallPlot
      ? SIDE_LABEL_MAX_WIDTH_SMALL
      : Math.max(
          SIDE_LABEL_MIN_CAP,
          Math.min(SIDE_LABEL_MAX_CAP, width * SIDE_LABEL_WIDTH_SHARE)
        );

    const rawTotalById = new Map<string, { in: number; out: number }>();
    for (const link of data?.links ?? []) {
      const source = rawTotalById.get(link.source) ?? { in: 0, out: 0 };
      source.out += link.value;
      rawTotalById.set(link.source, source);
      const target = rawTotalById.get(link.target) ?? { in: 0, out: 0 };
      target.in += link.value;
      rawTotalById.set(link.target, target);
    }

    const labelWidthOf = (node: Node): number => {
      if (labelFontSize === null) return 0;
      const raw = rawTotalById.get(node.id) ?? { in: 0, out: 0 };
      const labelWidth =
        getTextWidth({
          text: getSankeyNodeLabel(node),
          fontSize: labelFontSize,
        }) ?? sideLabelCap;
      const valueWidth = formatNodeValue
        ? (getTextWidth({
            text: formatNodeValue(Math.max(raw.in, raw.out)),
            fontSize: VALUE_FONT_SIZE,
          }) ?? 0)
        : 0;
      return Math.max(labelWidth, valueWidth);
    };
    const outerNodes = data ? outerColumnNodes(data) : { first: [], last: [] };
    const sideLabelWidths = [...outerNodes.first, ...outerNodes.last]
      .map(labelWidthOf)
      .sort((a, b) => a - b);
    const widestLabel = sideLabelWidths.at(-1) ?? 0;
    const fitWidth =
      sideLabelWidths[
        Math.max(
          0,
          Math.ceil(sideLabelWidths.length * SIDE_LABEL_FIT_SHARE) - 1
        )
      ] ?? 0;
    const sideLabelMaxWidth = Math.ceil(
      isSmallPlot
        ? Math.min(sideLabelCap, widestLabel)
        : Math.max(
            Math.min(widestLabel, SIDE_LABEL_MIN_CAP),
            Math.min(sideLabelCap, fitWidth)
          )
    );
    const leftMargin = sideLabelMaxWidth + LABEL_PADDING;
    const rightMargin = leftMargin;
    const plotWidth = width - leftMargin - rightMargin;
    const plotHeight =
      height - PLOT_TOP_PADDING - PLOT_BOTTOM_PADDING - bottomReserve;
    const isPlotSizeValid =
      hasData && hasViewport && plotWidth > 0 && plotHeight > 0;

    const numOfNodes = Math.max((data?.nodes.length ?? 1) - 1, 1);
    const nodePadding = Math.floor((height * 0.22) / numOfNodes);

    const flowTotalById = new Map<string, number>();
    const addFlow = (id: string, value: number) => {
      flowTotalById.set(id, (flowTotalById.get(id) ?? 0) + value);
    };
    for (const link of data?.links ?? []) {
      addFlow(link.source, link.value);
      addFlow(link.target, link.value);
    }

    const runLayout = (links: Link[]) =>
      sankey<Node, Link>()
        .nodeId((d) => d.id)
        .nodeAlign(nodeAlign)
        // Order every column by total node flow, largest at the top.
        .nodeSort(
          (a, b) =>
            (flowTotalById.get(b.id) ?? 0) - (flowTotalById.get(a.id) ?? 0)
        )
        .nodeWidth(NODE_WIDTH)
        .nodePadding(nodePadding)
        .extent([
          [leftMargin, PLOT_TOP_PADDING],
          [leftMargin + plotWidth, PLOT_TOP_PADDING + plotHeight],
        ])({ nodes: cloneDeep(data?.nodes ?? []), links });

    const rawValues = (data?.links ?? []).map((link) => link.value);

    const layoutLinks = (minValue: number, values: number[]): Link[] =>
      cloneDeep(data?.links ?? []).map((link, index) => ({
        ...link,
        rawValue: link.value,
        value: floorLinkValue(values[index] ?? 0, minValue),
      }));

    const prepareNodes = (
      nodes: DSankeyNode<Node, Link>[]
    ): SankeyNodeValidated[] => {
      const validated = validateNodes(nodes);
      for (const node of validated) {
        node.depth = node.layer ?? node.depth;
      }
      return validated;
    };

    const linearPass = isPlotSizeValid
      ? runLayout(layoutLinks(0, rawValues))
      : null;
    const linearNodes = linearPass ? prepareNodes(linearPass.nodes) : [];
    const maxDepth = linearNodes.reduce((max, node) => {
      return Math.max(max, node.depth);
    }, 0);

    const columnSize = (depth: number) => {
      let count = 0;
      for (const node of linearNodes) if (node.depth === depth) count++;
      return count;
    };

    const hubIsTargetByIndex = new Map<number, boolean>();
    for (const link of validateLinks(linearPass?.links ?? [])) {
      if (link.index === undefined) continue;
      hubIsTargetByIndex.set(link.index, resolveHubIsTarget(link, columnSize));
    }

    const hubSideKeys = (data?.links ?? []).map((link, index) => {
      const hubIsTarget = hubIsTargetByIndex.get(index);
      if (hubIsTarget === undefined) return undefined;
      return hubIsTarget ? `${link.target}::in` : `${link.source}::out`;
    });

    const depthById = new Map(
      linearNodes.map((node) => [node.id, `${node.depth}`])
    );
    const resolvedScale =
      autoScale && linearPass
        ? solveSankeyScale(
            {
              values: rawValues,
              sideKeys: hubSideKeys,
              linkGroups: (data?.links ?? []).map(
                (link) =>
                  `${depthById.get(link.source)}->${depthById.get(link.target)}`
              ),
              linkEnds: (data?.links ?? []).map((link) => ({
                source: link.source,
                target: link.target,
              })),
              nodeGroups: depthById,
              plotHeight,
              nodePadding,
            },
            inversionTolerance
          )
        : { totalExponent, shareExponent };

    const scaledValues =
      linearPass && !isLinearSankeyScale(resolvedScale)
        ? scaleLinkValues({
            values: rawValues,
            sideKeys: hubSideKeys,
            scale: resolvedScale,
          })
        : null;

    const layoutValues = scaledValues ?? rawValues;
    const scaledPass = scaledValues
      ? runLayout(layoutLinks(0, scaledValues))
      : linearPass;
    const scaledNodes = scaledValues
      ? prepareNodes(scaledPass?.nodes ?? [])
      : linearNodes;

    const pxPerValue = (() => {
      const priced = scaledPass?.links.find(
        (link) => (link.value ?? 0) > 0 && (link.width ?? 0) > 0
      );
      if (!priced?.width || !priced.value) return 0;
      const labelShrink = fillColumnHeight
        ? interiorLabelScale({
            nodes: scaledNodes,
            maxDepth,
            plotHeight,
            labelReserve: interiorLabelReserve,
          })
        : 1;
      return (priced.width / priced.value) * labelShrink;
    })();

    const minLinkValue = solveMinLinkValue({
      pxPerValue,
      maxLinkValue: layoutValues.reduce(
        (max, value) => Math.max(max, value),
        0
      ),
    });
    const needsFloor = layoutValues.some(
      (value) => value > 0 && value < minLinkValue
    );

    const { nodes, links } = (() => {
      if (!scaledPass) return { nodes: [], links: [] };
      return needsFloor
        ? runLayout(layoutLinks(minLinkValue, layoutValues))
        : scaledPass;
    })();

    const nodesValidated = needsFloor ? prepareNodes(nodes) : scaledNodes;
    const linksValidated = validateLinks(links);

    // Raw inflow/outflow totals per node id, for tooltips and node values.
    const rawByNode = new Map<string, SankeyNodeRaw>();
    const bump = (id: string, key: 'in' | 'out', amount: number) => {
      const entry = rawByNode.get(id) ?? { in: 0, out: 0 };
      entry[key] += amount;
      rawByNode.set(id, entry);
    };
    for (const link of linksValidated) {
      const raw = link.rawValue ?? link.value ?? 0;
      bump(link.source.id, 'out', raw);
      bump(link.target.id, 'in', raw);
    }

    const endpoints = (() => {
      if (!fillColumnHeight) return EMPTY_LINK_ENDPOINTS;
      const scale = interiorLabelScale({
        nodes: nodesValidated,
        maxDepth,
        plotHeight,
        labelReserve: interiorLabelReserve,
      });
      if (scale < 1) {
        for (const node of nodesValidated) {
          node.y1 = node.y0 + (node.y1 - node.y0) * scale;
        }
        for (const link of linksValidated) {
          link.width *= scale;
        }
      }
      return applyColumnFillLayout({
        nodes: nodesValidated,
        top: PLOT_TOP_PADDING,
        plotHeight,
      });
    })();

    // A small horizontal gap keeps ribbons off the node bars.
    const sourcePoint = (link: SankeyLinkValidated): [number, number] => [
      link.source.x1 + NODE_LINK_GAP,
      (link.index !== undefined
        ? endpoints.sourceY.get(link.index)
        : undefined) ?? link.y0,
    ];
    const targetPoint = (link: SankeyLinkValidated): [number, number] => [
      link.target.x0 - NODE_LINK_GAP,
      (link.index !== undefined
        ? endpoints.targetY.get(link.index)
        : undefined) ?? link.y1,
    ];

    const linkPathGenerator = sankeyLinkHorizontal<Node, SankeyLinkValidated>()
      .source(sourcePoint)
      .target(targetPoint);

    const renderLinks: SankeyRenderLink[] = linksValidated.flatMap(
      (link, i) => {
        const path = linkPathGenerator(link);
        if (path === null) return [];
        const rawValue = link.rawValue ?? link.value ?? 0;
        const sourceOutRaw = rawByNode.get(link.source.id)?.out ?? 0;
        const displayShare =
          link.share ??
          (sourceOutRaw > 0 ? rawValue / sourceOutRaw : undefined);
        const hubIsTarget =
          hubIsTargetByIndex.get(link.index ?? i) ??
          resolveHubIsTarget(link, columnSize);
        const [sourceX, sourceY] = sourcePoint(link);
        const [targetX, targetY] = targetPoint(link);
        return [
          {
            key: linkKeyFor(link),
            index: link.index ?? i,
            path,
            color: link.color,
            width: link.width,
            sourceX,
            sourceY,
            targetX,
            targetY,
            source: link.source,
            target: link.target,
            rawValue,
            displayShare,
            hubIsTarget,
            group: link.group,
          },
        ];
      }
    );

    const inversions = (() => {
      if (!showOrderInversions) {
        return {
          invertedLinkKeys: new Set<string>(),
          invertedNodeIds: new Set<string>(),
        };
      }
      const linkItems: OrderItem[] = renderLinks.map((link) => ({
        key: link.key,
        group: `${link.source.depth}->${link.target.depth}`,
        raw: link.rawValue,
        rendered: link.width,
      }));
      const nodeItems: OrderItem[] = nodesValidated.map((node) => {
        const raw = rawByNode.get(node.id) ?? { in: 0, out: 0 };
        return {
          key: node.id,
          group: `${node.depth}`,
          raw: Math.max(raw.in, raw.out),
          rendered: node.y1 - node.y0,
        };
      });
      return {
        invertedLinkKeys: findOrderInversions(linkItems, inversionTolerance),
        invertedNodeIds: findOrderInversions(nodeItems, inversionTolerance),
      };
    })();

    const interiorLabelMaxWidth = Math.max(
      40,
      plotWidth / (maxDepth + 1) - LABEL_PADDING
    );

    return {
      isPlotSizeValid,
      maxDepth,
      nodesValidated,
      renderLinks,
      rawByNode,
      interiorLabelMaxWidth,
      sideLabelMaxWidth,
      resolvedScale,
      ...inversions,
    };
  }, [
    data,
    width,
    height,
    totalExponent,
    shareExponent,
    autoScale,
    fillColumnHeight,
    interiorLabelReserve,
    bottomReserve,
    showOrderInversions,
    inversionTolerance,
    labelFontSize,
    formatNodeValue,
  ]);
};
